import { useState } from 'react';
import TecladoNumero from './TecladoNumero';
import type { ProdutoCaixa } from '../types';

interface Props {
  produto: ProdutoCaixa;
  atual: number;
  aoConfirmar: (quantidade: number) => void;
  aoFechar: () => void;
}

// Folha do teclado numérico: digitar a quantidade de um produto (ex.: 10 cervejas).
export default function FolhaQuantidade({ produto, atual, aoConfirmar, aoFechar }: Props) {
  const [texto, setTexto] = useState(atual > 0 ? String(atual) : '');

  const limite = produto.disponivel ?? produto.max_por_pedido ?? null;
  const quantidade = texto ? parseInt(texto, 10) : 0;
  const acimaDoLimite = limite != null && quantidade > limite;

  function digitar(d: string) {
    setTexto((t) => {
      const prox = (t + d).replace(/^0+/, '');
      return prox.slice(0, 4);
    });
  }

  function apagar() {
    setTexto((t) => t.slice(0, -1));
  }

  function confirmar() {
    let q = quantidade;
    if (limite != null && q > limite) q = limite;
    aoConfirmar(q);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 w-full max-w-[400px] rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-secondary-200 sm:hidden" />

        <div className="flex items-center justify-between px-5 pt-3">
          <h2 className="truncate pr-2 text-lg font-bold text-foreground-950">{produto.nome}</h2>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="text-center text-6xl font-extrabold tabular-nums text-foreground-950">
            {quantidade}
          </p>
          {limite != null ? (
            <p className={`mt-1 text-center text-sm ${acimaDoLimite ? 'text-danger-600' : 'text-secondary-500'}`}>
              {acimaDoLimite
                ? `Só há ${limite}.`
                : `Disponível: ${limite}`}
            </p>
          ) : (
            <p className="mt-1 text-center text-sm text-secondary-500">
              Quantas unidades?
            </p>
          )}

          <div className="mt-4">
            <TecladoNumero
              aoDigitar={digitar}
              aoApagar={apagar}
              aoOk={confirmar}
              rotuloOk="OK"
              okDesabilitado={quantidade === 0}
            />
          </div>

          {atual > 0 && (
            <button
              type="button"
              onClick={() => aoConfirmar(0)}
              className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-xl border border-danger-200 bg-white py-3 text-sm font-semibold text-danger-600 transition-colors hover:bg-danger-50"
            >
              Tirar do pedido
            </button>
          )}
        </div>
      </div>
    </div>
  );
}